import {
  buildPhaseTransitionSafetySnapshot,
  collectPhaseTransitionSafetyIssues,
} from './riskProfileFlags';
import { sha256Hex } from './journeyState';
import { canonicalJsonbText } from './canonicalJsonb';
import { classifyAge } from './clinicalClearance';
import {
  DIABETE_OPTIONS,
  DIGESTIF_OPTIONS,
  MICI_STATUT_OPTIONS,
  THYROIDE_OPTIONS,
  THYROIDE_STATUT_OPTIONS,
} from './clinical/clinicalFieldOptions';
import {
  NO_FOOD_RESTRICTION,
  RESTRICTIONS_MAP,
} from './prompts/nutrition/restrictions.fr';
import { OUVERTURE_COMPLEMENTS } from './prompts/nutrition/anamneseFoundation';
export { canonicalJsonbText } from './canonicalJsonb';

export const FOLLOWUP_SAFETY_SNAPSHOT_VERSION = 1;

const FREE_TEXT_FIELDS = Object.freeze([
  'allergies',
  'alimentsEvites',
  'pathologies',
  'traitements',
  'medicaments',
  'traitementsActuels',
  'traitementsDetail',
  'traitementsMomentPrise',
  'traitementsVerifiesParAnissa',
  'traitementsVerifiesLe',
  'diabeteDiagnostic',
  'miciStatut',
  'miciStatutPour',
  'thyroideDiagnostic',
  'thyroideStatut',
  'restrictionsAutre',
  'ouvertureComplements',
  'objectifPrincipalNutrition',
  'objectifPrincipal',
  'objectif_primaire',
  'dureeProbleme',
  'commentaires',
  'commentaire',
  'notes',
  'symptomes',
  'contexteEmotionnel',
]);

const CORE_REQUIRED_TEXT_FIELDS = Object.freeze([
  'allergies',
  'alimentsEvites',
  'pathologies',
  'traitementsActuels',
  'traitementsVerifiesParAnissa',
  'traitementsVerifiesLe',
]);

// Les deux preuves sont regroupées sous un seul raccord lisible. Le serveur
// les exige toutes deux avant de construire le snapshot.
const ATTESTATION_FIELDS = new Set(['traitementsVerifiesParAnissa', 'traitementsVerifiesLe']);

const EXPLICIT_CONFIRMATION_FIELDS = Object.freeze([
  'allergies',
  'alimentsEvites',
  'pathologies',
  'diabeteDiagnostic',
  'thyroideDiagnostic',
  'genre',
]);

const optionValues = (options) => new Set(options.map((option) => option.value));
const DIABETE_VALUES = optionValues(DIABETE_OPTIONS);
const DIGESTIF_VALUES = optionValues(DIGESTIF_OPTIONS);
const MICI_VALUES = optionValues(MICI_STATUT_OPTIONS);
const THYROIDE_VALUES = optionValues(THYROIDE_OPTIONS);
const THYROIDE_STATUT_VALUES = optionValues(THYROIDE_STATUT_OPTIONS);
const RESTRICTION_VALUES = new Set([NO_FOOD_RESTRICTION, ...Object.keys(RESTRICTIONS_MAP)]);

const FIELD_GUIDANCE = Object.freeze({
  form: { label: 'Fiche de sécurité', section: 'Anamnèse' },
  now: { label: 'Date de contrôle du dossier', section: 'Anamnèse' },
  age: { label: 'Âge ou date de naissance', section: 'Anamnèse · Validation & Mesures' },
  ageActuel: { label: 'Âge actuel', section: 'Anamnèse · Validation & Mesures' },
  dateNaissance: { label: 'Date de naissance', section: 'Anamnèse · Validation & Mesures' },
  date_naissance: { label: 'Date de naissance', section: 'Anamnèse · Validation & Mesures' },
  genre: { label: 'Genre', section: 'Anamnèse · Validation & Mesures' },
  allergies: { label: 'Allergies', section: 'Anamnèse · Validation & Mesures' },
  alimentsEvites: { label: 'Aliments évités / intolérances', section: 'Anamnèse · Digestion' },
  pathologies: { label: 'Pathologies', section: 'Anamnèse · Validation & Mesures' },
  diabeteDiagnostic: { label: 'Diagnostic diabète', section: 'Anamnèse · Santé et suivi médical' },
  digestifDiagnostic: { label: 'Troubles digestifs diagnostiqués', section: 'Anamnèse · Santé et suivi médical' },
  miciStatut: { label: 'État actuel de la MICI', section: 'Anamnèse · Santé et suivi médical' },
  thyroideDiagnostic: { label: 'Diagnostic thyroïde', section: 'Anamnèse · Santé et suivi médical' },
  thyroideStatut: { label: 'Situation thyroïdienne stabilisée', section: 'Anamnèse · Santé et suivi médical' },
  traitementsActuels: { label: 'Traitements médicamenteux en cours', section: 'Anamnèse · Santé et suivi médical' },
  traitementsDetail: { label: 'Détail des traitements', section: 'Anamnèse · Santé et suivi médical' },
  traitementsVerification: { label: 'Relecture des traitements', section: 'Anamnèse · Santé et suivi médical' },
  complementsActuels: { label: 'Compléments actuellement pris', section: 'Anamnèse · Santé et suivi médical' },
  ouvertureComplements: { label: 'Ouverture aux compléments', section: 'Anamnèse · Santé et suivi médical' },
  phaseSafety: { label: 'Grossesse, allaitement, post-partum et profils à risque', section: 'Anamnèse · Santé femme / Validation & Mesures' },
  grossesseActuelle: { label: 'Grossesse en cours', section: 'Anamnèse · Santé femme' },
  allaitement: { label: 'Allaitement', section: 'Anamnèse · Santé femme' },
  postPartum: { label: 'Post-partum', section: 'Anamnèse · Santé femme' },
  profilsRisqueVerifiesParAnissa: { label: 'Relecture des profils à risque', section: 'Anamnèse · Validation & Mesures' },
  flagsProfilRisque: { label: 'Profils à risque', section: 'Anamnèse · Validation & Mesures' },
  restrictionsAlimentaires: { label: 'Restrictions alimentaires', section: 'Anamnèse · Santé et suivi médical' },
  restrictionsAutre: { label: 'Autre restriction alimentaire', section: 'Anamnèse · Santé et suivi médical' },
  ramadanActif: { label: 'Absence d’adaptation Ramadan', section: 'Anamnèse · Santé et suivi médical' },
  presentationSansChiffres: { label: 'Présentation du programme', section: 'Anamnèse · Santé et suivi médical' },
});

// Deux gravités seulement, et le défaut est le côté prudent : une règle dont
// on aurait oublié de décider reste bloquante.
export const SAFETY_SEVERITY = Object.freeze({
  BLOCKING: 'blocking',
  TO_CONFIRM: 'to_confirm',
});

function validationIssue(field, message, severity = SAFETY_SEVERITY.BLOCKING) {
  const guidance = FIELD_GUIDANCE[field] || {
    label: field,
    section: 'Anamnèse',
  };
  return Object.freeze({
    field,
    label: guidance.label,
    section: guidance.section,
    message,
    severity,
  });
}

function pushUniqueIssue(issues, field, message, severity = SAFETY_SEVERITY.BLOCKING) {
  if (!issues.some((issue) => issue.field === field)) {
    issues.push(validationIssue(field, message, severity));
  }
}

/** Ne retient que ce qui doit réellement arrêter Anissa. */
export function blockingSafetyIssues(issues) {
  return (issues || []).filter((issue) => issue.severity !== SAFETY_SEVERITY.TO_CONFIRM);
}

/** Ce qui est signalé sans rien empêcher. */
export function confirmationSafetyIssues(issues) {
  return (issues || []).filter((issue) => issue.severity === SAFETY_SEVERITY.TO_CONFIRM);
}

function captureIssue(issues, field, callback, severity = SAFETY_SEVERITY.BLOCKING) {
  try {
    callback();
  } catch (error) {
    pushUniqueIssue(issues, field, error?.message || String(error), severity);
  }
}

export class FollowupSafetyPreflightError extends Error {
  constructor(issues) {
    super('À valider Anissa — l’anamnèse doit être complétée avant de générer les menus et recettes.');
    this.name = 'FollowupSafetyPreflightError';
    this.issues = Object.freeze([...(issues || [])]);
  }
}

// Contrat partagé avec V97.204 : normalisation CRLF puis retrait des seuls
// espaces ASCII de bord. Les caractères Unicode ne sont jamais modifiés à
// l'insu d'Anissa et la preuve navigateur/SQL reste bit-à-bit identique.
const normalizeText = (value) => String(value ?? '')
  .replace(/\r\n/g, '\n')
  .replace(/^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g, '');

function resolveSnapshotOuvertureComplements(value) {
  // Même normalisation que SQL : les espaces Unicode ne sont pas retirés.
  // Une valeur que le serveur refuserait doit être corrigée avant le RPC.
  const normalized = normalizeText(value);
  if (!normalized) return null;
  if (Object.hasOwn(OUVERTURE_COMPLEMENTS, normalized)) return normalized;
  const lower = normalized.toLowerCase();
  return Object.entries(OUVERTURE_COMPLEMENTS)
    .find(([, label]) => label.toLowerCase() === lower)?.[0] || null;
}

function normalizeStrictStringList(value, label, { allowEmpty = false, allowedValues = null } = {}) {
  if (!Array.isArray(value)) {
    throw new Error(`À valider Anissa — ${label} doit être relu dans son champ structuré.`);
  }
  const normalized = value.map((item) => {
    if (typeof item !== 'string' || !normalizeText(item)) {
      throw new Error(`À valider Anissa — ${label} contient une valeur ambiguë.`);
    }
    return normalizeText(item);
  });
  if (!allowEmpty && normalized.length === 0) {
    throw new Error(`À valider Anissa — ${label} ne peut pas être vide ; confirme explicitement « aucun » si nécessaire.`);
  }
  if (new Set(normalized).size !== normalized.length
      || (allowedValues && normalized.some((item) => !allowedValues.has(item)))) {
    throw new Error(`À valider Anissa — ${label} contient une valeur inconnue ou dupliquée.`);
  }
  return normalized;
}

function normalizeComplements(value) {
  if (!Array.isArray(value)) {
    throw new Error('À valider Anissa — renseigne « Compléments alimentaires actuellement pris » dans la section « Santé et suivi médical » de l’anamnèse.');
  }
  if (value.length === 0) {
    throw new Error('À valider Anissa — confirme explicitement les compléments actuellement pris, ou indique « Aucun ».');
  }
  const normalized = value.map((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)
        || Object.keys(item).some((key) => key !== 'nom' && key !== 'dose')
        || typeof item.nom !== 'string' || !normalizeText(item.nom)
        || (Object.hasOwn(item, 'dose') && typeof item.dose !== 'string')) {
      throw new Error(`À valider Anissa — le complément ${index + 1} a un format ambigu.`);
    }
    return Object.freeze({ nom: normalizeText(item.nom), dose: normalizeText(item.dose) });
  });
  const aucun = normalized.filter((item) => item.nom.toLowerCase() === 'aucun');
  if (aucun.length > 0 && (normalized.length !== 1 || aucun[0].dose)) {
    throw new Error('À valider Anissa — « Aucun » doit être la seule réponse et ne peut pas porter de dose.');
  }
  return normalized;
}

function normalizeDeclaredAge(value, field) {
  if (value == null || value === '') return '';
  const raw = typeof value === 'number' ? String(value) : normalizeText(value);
  if (!/^\d{1,3}$/.test(raw)) {
    throw new Error(`À valider Anissa — le champ ${field} a un format ambigu.`);
  }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 120) {
    throw new Error(`À valider Anissa — le champ ${field} est hors format attendu.`);
  }
  return String(parsed);
}

function ageFromDob(dob, now) {
  const birth = new Date(`${dob}T00:00:00Z`);
  let years = now.getUTCFullYear() - birth.getUTCFullYear();
  const beforeBirthday = (now.getUTCMonth() < birth.getUTCMonth())
    || (now.getUTCMonth() === birth.getUTCMonth() && now.getUTCDate() < birth.getUTCDate());
  if (beforeBirthday) years -= 1;
  return years;
}

function resolveAgeEvidence(form, now) {
  const dobPrimary = Object.hasOwn(form, 'dateNaissance') ? form.dateNaissance : '';
  const dobAlias = Object.hasOwn(form, 'date_naissance') ? form.date_naissance : '';
  for (const [field, value] of [['dateNaissance', dobPrimary], ['date_naissance', dobAlias]]) {
    if (value != null && value !== '' && (typeof value !== 'string' || !isExactIsoDate(normalizeText(value)))) {
      throw new Error(`À valider Anissa — le champ ${field} n'est pas une date ISO valide.`);
    }
  }
  const normalizedPrimary = normalizeText(dobPrimary);
  const normalizedAlias = normalizeText(dobAlias);
  if (normalizedPrimary && normalizedAlias && normalizedPrimary !== normalizedAlias) {
    throw new Error('À valider Anissa — les deux dates de naissance du dossier sont contradictoires.');
  }
  const dateNaissance = normalizedPrimary || normalizedAlias;
  const age = normalizeDeclaredAge(form.age, 'age');
  const ageActuel = normalizeDeclaredAge(form.ageActuel, 'ageActuel');
  if (age && ageActuel && age !== ageActuel) {
    throw new Error('À valider Anissa — les âges déclarés du dossier sont contradictoires.');
  }
  if (classifyAge(form, now) === 'unknown') {
    throw new Error("À valider Anissa — l'âge du dossier est absent ou ambigu.");
  }
  if (dateNaissance) {
    // La date de naissance fait foi. Un âge saisi à l'anamnèse prend un an de
    // retard à chaque anniversaire de la cliente pendant son suivi : ce retard
    // d'exactement un an est normal et ne bloque plus (cas Guillemette, 13.10).
    // Tout autre écart peut trahir une date mal saisie et reste à valider.
    const derived = ageFromDob(dateNaissance, now);
    const coherent = (declared) => !declared
      || Number(declared) === derived
      || Number(declared) === derived - 1;
    if (!coherent(age) || !coherent(ageActuel)) {
      throw new Error("À valider Anissa — l'âge déclaré ne correspond pas à la date de naissance.");
    }
  } else if (!age && !ageActuel) {
    throw new Error("À valider Anissa — la date de naissance ou l'âge confirmé est requis.");
  }
  return Object.freeze({ dateNaissance, age, ageActuel });
}

function isExactIsoDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/**
 * Retourne en une seule passe toutes les entrées qui empêchent la création du
 * snapshot de sécurité. C'est la source canonique du précontrôle UX : le
 * constructeur ci-dessous consomme exactement cette liste et reste fermé dès
 * qu'elle n'est pas vide.
 *
 * Aucun correctif ni valeur par défaut n'est appliqué. Les anciens dossiers
 * restent donc explicitement à relire par Anissa.
 */
export function collectFollowupSafetyValidationIssuesV1(form, now = new Date()) {
  const issues = [];
  if (!form || typeof form !== 'object' || Array.isArray(form)) {
    return Object.freeze([
      validationIssue('form', 'À valider Anissa — la fiche de sécurité est indisponible.'),
    ]);
  }

  if (!(now instanceof Date) || Number.isNaN(now.getTime())) {
    pushUniqueIssue(issues, 'now', 'À valider Anissa — la date de contrôle du dossier est indisponible.');
  }

  for (const field of FREE_TEXT_FIELDS) {
    if (Object.hasOwn(form, field) && typeof form[field] !== 'string') {
      pushUniqueIssue(issues, field, `À valider Anissa — le champ ${field} a un format ambigu.`);
    }
  }
  for (const field of ['genre', 'dateNaissance', 'date_naissance']) {
    if (Object.hasOwn(form, field) && typeof form[field] !== 'string') {
      pushUniqueIssue(issues, field, `À valider Anissa — le champ ${field} a un format ambigu.`);
    }
  }
  for (const field of ['age', 'ageActuel']) {
    if (Object.hasOwn(form, field)
        && typeof form[field] !== 'string'
        && typeof form[field] !== 'number') {
      pushUniqueIssue(issues, field, `À valider Anissa — le champ ${field} a un format ambigu.`);
    }
  }
  for (const field of CORE_REQUIRED_TEXT_FIELDS) {
    if (!Object.hasOwn(form, field) || typeof form[field] !== 'string') {
      const estAttestation = ATTESTATION_FIELDS.has(field);
      pushUniqueIssue(
        issues,
        estAttestation ? 'traitementsVerification' : field,
        estAttestation
          ? 'À valider Anissa — les traitements et médicaments doivent être marqués comme vérifiés et datés.'
          : `À valider Anissa — le champ ${field} doit être relu dans la fiche.`,
        SAFETY_SEVERITY.BLOCKING,
      );
    }
  }
  for (const field of EXPLICIT_CONFIRMATION_FIELDS) {
    if (!normalizeText(form[field])) {
      pushUniqueIssue(
        issues,
        field,
        `À valider Anissa — le champ ${field} ne peut pas être vide ; confirme explicitement « aucun » si nécessaire.`,
      );
    }
  }

  const preciseAgeFields = new Set(['age', 'ageActuel', 'dateNaissance', 'date_naissance']);
  if (!issues.some((issue) => preciseAgeFields.has(issue.field))) {
    captureIssue(issues, 'age', () => resolveAgeEvidence(form, now));
  }

  if (!new Set(['oui', 'non']).has(normalizeText(form.traitementsActuels).toLowerCase())) {
    pushUniqueIssue(issues, 'traitementsActuels', 'À valider Anissa — confirme si un traitement ou médicament est actuellement pris.');
  }
  if (
    normalizeText(form.traitementsVerifiesParAnissa).toLowerCase() !== 'oui'
    || !isExactIsoDate(normalizeText(form.traitementsVerifiesLe))
  ) {
    pushUniqueIssue(issues, 'traitementsVerification', 'À valider Anissa — les traitements et médicaments doivent être marqués comme vérifiés et datés.');
  }
  if (
    normalizeText(form.traitementsActuels).toLowerCase() === 'oui'
    && !normalizeText(form.traitementsDetail)
  ) {
    pushUniqueIssue(issues, 'traitementsDetail', 'À valider Anissa — le détail des traitements ou médicaments est manquant.');
  }
  if (!DIABETE_VALUES.has(normalizeText(form.diabeteDiagnostic))) {
    pushUniqueIssue(issues, 'diabeteDiagnostic', 'À valider Anissa — le diagnostic diabète doit être confirmé dans le champ structuré.');
  }
  if (!THYROIDE_VALUES.has(normalizeText(form.thyroideDiagnostic))) {
    pushUniqueIssue(issues, 'thyroideDiagnostic', 'À valider Anissa — le diagnostic thyroïde doit être confirmé dans le champ structuré.');
  }
  if (!resolveSnapshotOuvertureComplements(form.ouvertureComplements)) {
    pushUniqueIssue(
      issues,
      'ouvertureComplements',
      normalizeText(form.ouvertureComplements)
        ? 'À valider Anissa — « Ouverture aux compléments » contient une valeur non reconnue. Choisis « éviter », « si nécessaire » ou « à l’aise ».'
        : 'À valider Anissa — « Ouverture aux compléments » n’est pas renseignée dans la section « Santé et suivi médical ».',
    );
  }

  for (const field of ['grossesseActuelle', 'allaitement', 'postPartum']) {
    if (!['Oui', 'Non'].includes(form[field])) {
      pushUniqueIssue(issues, field, `À valider Anissa — le champ ${field} doit être exactement « Oui » ou « Non ».`);
    }
  }
  for (const issue of collectPhaseTransitionSafetyIssues(form)) {
    pushUniqueIssue(
      issues,
      issue.field,
      `À valider Anissa — ${issue.message}`,
    );
  }

  let digestifDiagnostic = null;
  captureIssue(issues, 'digestifDiagnostic', () => {
    digestifDiagnostic = normalizeStrictStringList(form.digestifDiagnostic, 'le diagnostic digestif', {
      allowedValues: DIGESTIF_VALUES,
    });
  });
  if (digestifDiagnostic?.some((item) => ['crohn', 'rch', 'autreMici'].includes(item))
      && !MICI_VALUES.has(normalizeText(form.miciStatut))) {
    pushUniqueIssue(issues, 'miciStatut', 'À valider Anissa — le statut MICI doit être confirmé dans le champ structuré.');
  }
  if (['hyperthyroidie', 'basedow'].includes(normalizeText(form.thyroideDiagnostic))
      && !THYROIDE_STATUT_VALUES.has(normalizeText(form.thyroideStatut))) {
    pushUniqueIssue(issues, 'thyroideStatut', 'À valider Anissa — le statut thyroïdien doit être confirmé dans le champ structuré.');
  }

  let restrictionsAlimentaires = null;
  captureIssue(issues, 'restrictionsAlimentaires', () => {
    restrictionsAlimentaires = normalizeStrictStringList(
      form.restrictionsAlimentaires,
      'les restrictions alimentaires',
      { allowedValues: RESTRICTION_VALUES },
    );
    if (restrictionsAlimentaires.includes(NO_FOOD_RESTRICTION)
        && restrictionsAlimentaires.length !== 1) {
      throw new Error('À valider Anissa — « Aucune restriction » ne peut pas être associée à une autre restriction.');
    }
  });
  if (restrictionsAlimentaires?.includes('autre') && !normalizeText(form.restrictionsAutre)) {
    pushUniqueIssue(issues, 'restrictionsAutre', 'À valider Anissa — précise la restriction alimentaire « autre ».');
  } else if (restrictionsAlimentaires && !restrictionsAlimentaires.includes('autre')
      && normalizeText(form.restrictionsAutre)) {
    pushUniqueIssue(issues, 'restrictionsAutre', 'À valider Anissa — un détail de restriction est renseigné sans que « Autre préférence » soit coché.');
  }

  if (typeof form.ramadanActif !== 'boolean') {
    pushUniqueIssue(issues, 'ramadanActif', 'À valider Anissa — confirme explicitement si une adaptation Ramadan est active.');
  }
  if (typeof form.presentationSansChiffres !== 'boolean') {
    pushUniqueIssue(issues, 'presentationSansChiffres', 'À valider Anissa — choisis explicitement le mode de présentation du programme.');
  }
  if (Array.isArray(form.flagsProfilRisque)) {
    captureIssue(issues, 'flagsProfilRisque', () => normalizeStrictStringList(
      form.flagsProfilRisque,
      'les profils à risque',
      { allowEmpty: true },
    ));
  }
  captureIssue(issues, 'complementsActuels', () => normalizeComplements(form.complementsActuels));

  return Object.freeze(issues);
}

export function assertFollowupSafetyPreflightV1(form, now = new Date()) {
  const bloquantes = blockingSafetyIssues(collectFollowupSafetyValidationIssuesV1(form, now));
  if (bloquantes.length > 0) throw new FollowupSafetyPreflightError(bloquantes);
}

/**
 * Ce qui est signalé à Anissa sans jamais l'arrêter. Une anamnèse se remplit
 * pendant la consultation : rien de purement déclaratif ne doit la bloquer
 * devant sa cliente.
 */
export function collectFollowupSafetyConfirmationsV1(form, now = new Date()) {
  return Object.freeze(confirmationSafetyIssues(
    collectFollowupSafetyValidationIssuesV1(form, now),
  ));
}

/**
 * Snapshot fermé des seules entrées qui alimentent le bloc sécurité existant,
 * plus les pathologies et le statut de vérification des traitements. Il ne
 * déduit aucune règle clinique : toute donnée centrale absente ou ambiguë
 * ferme le flux et renvoie Anissa vers la fiche.
 */
export function buildFollowupSafetySnapshotV1(form, now = new Date()) {
  const bloquantes = blockingSafetyIssues(collectFollowupSafetyValidationIssuesV1(form, now));
  if (bloquantes.length > 0) throw new Error(bloquantes[0].message);
  const phase = buildPhaseTransitionSafetySnapshot(form);
  const ageEvidence = resolveAgeEvidence(form, now);
  const digestifDiagnostic = normalizeStrictStringList(form.digestifDiagnostic, 'le diagnostic digestif', {
    allowedValues: DIGESTIF_VALUES,
  });
  const restrictionsAlimentaires = normalizeStrictStringList(
    form.restrictionsAlimentaires,
    'les restrictions alimentaires',
    { allowedValues: RESTRICTION_VALUES },
  );
  const text = Object.fromEntries(FREE_TEXT_FIELDS.map((field) => [field, normalizeText(form[field])]));
  return Object.freeze({
    version: FOLLOWUP_SAFETY_SNAPSHOT_VERSION,
    ...text,
    ...ageEvidence,
    genre: normalizeText(form.genre),
    traitementsActuels: normalizeText(form.traitementsActuels).toLowerCase(),
    // Le snapshot ne doit jamais affirmer une vérification qui n'a pas eu lieu.
    traitementsVerifiesParAnissa: normalizeText(form.traitementsVerifiesParAnissa).toLowerCase(),
    ouvertureComplements: resolveSnapshotOuvertureComplements(form.ouvertureComplements) || '',
    grossesseActuelle: phase.grossesseActuelle,
    allaitement: phase.allaitement,
    postPartum: phase.postPartum,
    profilsRisqueVerifiesParAnissa: form.profilsRisqueVerifiesParAnissa === true,
    flagsProfilRisque: Object.freeze(
      Array.isArray(form.flagsProfilRisque)
        ? normalizeStrictStringList(form.flagsProfilRisque, 'les profils à risque', { allowEmpty: true })
        : [],
    ),
    digestifDiagnostic: Object.freeze(digestifDiagnostic),
    restrictionsAlimentaires: Object.freeze(restrictionsAlimentaires),
    // Défauts prudents quand le choix n'a pas été posé : aucune adaptation
    // Ramadan, et les quantités restent affichées.
    ramadanActif: form.ramadanActif === true,
    complementsActuels: normalizeComplements(form.complementsActuels),
    presentationSansChiffres: form.presentationSansChiffres === true,
  });
}

export async function hashFollowupSafetySnapshotV1(snapshot) {
  if (!snapshot || snapshot.version !== FOLLOWUP_SAFETY_SNAPSHOT_VERSION) {
    throw new Error('À valider Anissa — snapshot de sécurité invalide.');
  }
  return sha256Hex(canonicalJsonbText(snapshot));
}

export async function buildFollowupSafetyProofV1(form) {
  const snapshot = buildFollowupSafetySnapshotV1(form);
  return Object.freeze({
    snapshot,
    sha256: await hashFollowupSafetySnapshotV1(snapshot),
  });
}
